import * as React from 'react';
import { cn } from '../cn';

export type Tone = 'gray' | 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'teal' | 'indigo' | 'yellow';

export const toneClasses: Record<Tone, string> = {
  gray: 'bg-gray-tint text-text-2',
  blue: 'bg-accent-tint text-accent',
  green: 'bg-green-tint text-[color-mix(in_srgb,var(--green)_75%,var(--text))]',
  orange: 'bg-orange-tint text-[color-mix(in_srgb,var(--orange)_80%,var(--text))]',
  red: 'bg-red-tint text-red',
  purple: 'bg-purple-tint text-purple',
  teal: 'bg-teal-tint text-teal',
  indigo: 'bg-indigo-tint text-indigo',
  yellow: 'bg-yellow-tint text-[color-mix(in_srgb,var(--yellow)_55%,var(--text))]',
};

export const toneDot: Record<Tone, string> = {
  gray: 'bg-gray',
  blue: 'bg-accent',
  green: 'bg-green',
  orange: 'bg-orange',
  red: 'bg-red',
  purple: 'bg-purple',
  teal: 'bg-teal',
  indigo: 'bg-indigo',
  yellow: 'bg-yellow',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  size?: 'sm' | 'md';
  dot?: boolean;
}
export function Badge({ tone = 'gray', size = 'md', dot, className, children, ...props }: BadgeProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap', size === 'sm' ? 'h-5 px-2 text-[11px]' : 'h-6 px-2.5 text-[12px]', toneClasses[tone], className)} {...props}>
      {dot ? <span className={cn('size-1.5 rounded-full', toneDot[tone])} /> : null}
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  draft: 'gray',
  lead: 'purple',
  sent: 'blue',
  scheduled: 'blue',
  confirmed: 'blue',
  en_route: 'teal',
  in_progress: 'orange',
  completed: 'green',
  done: 'green',
  paid: 'green',
  active: 'green',
  applied: 'green',
  approved: 'green',
  accepted: 'green',
  received: 'green',
  matched: 'green',
  reconciled: 'green',
  recorded: 'blue',
  succeeded: 'green',
  invoiced: 'indigo',
  overdue: 'red',
  void: 'red',
  cancelled: 'red',
  declined: 'red',
  rejected: 'red',
  failed: 'red',
  bounced: 'red',
  no_show: 'red',
  error: 'red',
  revoked: 'red',
  refunded: 'orange',
  partially_paid: 'orange',
  pending: 'orange',
  proposed: 'orange',
  unmatched: 'orange',
  expired: 'gray',
  excluded: 'gray',
  archived: 'gray',
  inactive: 'gray',
  skipped: 'gray',
  running: 'blue',
  auto: 'green',
  propose: 'orange',
};

export function statusTone(status: string): Tone {
  return STATUS_TONES[status] ?? 'gray';
}
export function humanizeStatus(status: string): string {
  const s = status.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function StatusPill({ status, size = 'md', className, label }: { status: string; size?: 'sm' | 'md'; className?: string; label?: string }) {
  return (
    <Badge tone={statusTone(status)} size={size} dot className={className}>
      {label ?? humanizeStatus(status)}
    </Badge>
  );
}
